import { AppError } from "../core/errors";
import { createId } from "../core/ids";
import { CURRENT_PROJECT_VERSION, PROJECT_FORMAT, presetSchema, projectObjectSchema, projectSchema, setlistSchema, type Setlist, type Preset, type Project } from "./schema";

export const MAX_PROJECT_BYTES = 5 * 1024 * 1024;

type Doc = Record<string, unknown>;
export type Migration = (doc: Doc) => Doc;

/**
 * Migrations from version N to N+1, keyed by N. Pure; never drop user data
 * silently (move unknown/obsolete data into `extensions`).
 */
export const PROJECT_MIGRATIONS: Record<number, Migration> = {
  // v1 → v2: count-in added (default off).
  1: (doc) => {
    const m = doc.metronome;
    return typeof m === "object" && m !== null && !Array.isArray(m) ? { ...doc, metronome: { countInBars: 0, ...(m as Doc) } } : doc;
  },
  // v2 → v3: song notes and reference links added (empty).
  2: (doc) => {
    const s = doc.song;
    return typeof s === "object" && s !== null && !Array.isArray(s) ? { ...doc, song: { notes: "", references: [], ...(s as Doc) } } : doc;
  },
  // v3 → v4: new instrument kind "multisample" only; existing documents are unchanged.
  // The bump makes older app versions refuse v4 files instead of failing on an unknown kind.
  3: (doc) => doc,
};

export function migrateProject(doc: Doc, migrations: Record<number, Migration> = PROJECT_MIGRATIONS, target = CURRENT_PROJECT_VERSION): Doc {
  let v = doc.schemaVersion;
  if (typeof v !== "number" || !Number.isInteger(v) || v < 1) throw new AppError("PROJECT_INVALID", "Project has no valid schemaVersion.");
  if (v > target) throw new AppError("PROJECT_UNSUPPORTED_VERSION", `Project was saved by a newer version (schema ${v}; this app supports ${target}).`);
  let cur = doc;
  while (v < target) {
    const m = migrations[v];
    if (!m) throw new AppError("PROJECT_UNSUPPORTED_VERSION", `No migration from schema ${v}.`);
    cur = { ...m(cur), schemaVersion: v + 1 };
    v++;
  }
  return cur;
}

const KNOWN_KEYS = new Set(Object.keys(projectObjectSchema.shape));

function formatIssues(issues: { path: PropertyKey[]; message: string }[]): string {
  return issues.slice(0, 5).map((i) => `${i.path.map(String).join(".") || "(root)"}: ${i.message}`).join("; ");
}

/** Parse untrusted input (string or object) → validated, migrated Project. */
export function parseProject(input: unknown): Project {
  let raw: unknown = input;
  if (typeof input === "string") {
    if (input.length > MAX_PROJECT_BYTES) throw new AppError("PROJECT_TOO_LARGE", "Project file is larger than 5 MB.");
    try { raw = JSON.parse(input); } catch (e) { throw new AppError("PROJECT_INVALID", "File is not valid JSON.", { cause: e }); }
  }
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) throw new AppError("PROJECT_INVALID", "Project must be a JSON object.");
  const doc = raw as Doc;
  if (doc.format !== PROJECT_FORMAT) throw new AppError("PROJECT_INVALID", "Not a project file for this application (unexpected format id).");
  const migrated = migrateProject(doc);
  // Preserve unknown top-level fields under `extensions`.
  const extensions: Doc = typeof migrated.extensions === "object" && migrated.extensions !== null && !Array.isArray(migrated.extensions) ? { ...(migrated.extensions as Doc) } : {};
  for (const [k, v] of Object.entries(migrated)) if (!KNOWN_KEYS.has(k)) extensions[k] = v;
  const result = projectSchema.safeParse({ ...migrated, extensions });
  if (!result.success) throw new AppError("PROJECT_INVALID", `Project failed validation: ${formatIssues(result.error.issues)}`);
  return result.data;
}

export function serializeProject(p: Project): string {
  return JSON.stringify(p, null, 2);
}

/** Give an imported project a new identity (used when its id collides with an existing one). */
export function withNewIdentity(p: Project, now = new Date()): Project {
  return { ...p, id: createId(), revision: 0, createdAt: now.toISOString(), updatedAt: now.toISOString() };
}

export function parseSetlist(input: unknown): Setlist {
  let raw = input;
  if (typeof input === "string") {
    if (input.length > 512 * 1024) throw new AppError("PROJECT_TOO_LARGE", "Setlist file is too large.");
    try { raw = JSON.parse(input); } catch (e) { throw new AppError("PROJECT_INVALID", "Setlist is not valid JSON.", { cause: e }); }
  }
  const r = setlistSchema.safeParse(raw);
  if (!r.success) throw new AppError("PROJECT_INVALID", `Setlist failed validation: ${formatIssues(r.error.issues)}`);
  return r.data;
}

export function parsePreset(input: unknown): Preset {
  let raw = input;
  if (typeof input === "string") {
    if (input.length > 256 * 1024) throw new AppError("PROJECT_TOO_LARGE", "Preset file is too large.");
    try { raw = JSON.parse(input); } catch (e) { throw new AppError("PROJECT_INVALID", "Preset is not valid JSON.", { cause: e }); }
  }
  const r = presetSchema.safeParse(raw);
  if (!r.success) throw new AppError("PROJECT_INVALID", `Preset failed validation: ${formatIssues(r.error.issues)}`);
  return r.data;
}
